// frontend/src/components/EditCustomerModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Leon du Plooy
// Updated:      2026-09-15 08:39 BST
// Stage 1:    2026-10-26 14:16 GMT — Add independent Is Vendor field; preserve CRM lifecycle.
//
// Description:  Modal for editing customer profiles with compact layout.
//               Uses RESOURCES (internal staff) for Account Manager & Closest Resource.
//
// Mobile Responsiveness (2026-09-15):
//   • Existing desktop field groupings are retained from the sm breakpoint upward.
//   • Multi-column rows collapse to one column on phones.
//   • Inputs/selects use touch-safe minimum heights and safe min-width handling.
//   • Textareas remain usable without horizontal overflow.
//   • Primary action becomes full-width on phones and retains desktop alignment.
//
// Tenant Security (2026-09-15):
//   • Removes frontend propagation of tenant_id from the customer update payload.
//   • Tenant scope remains backend-owned via authenticated user/JWT context.
//
// Operational Notes:
//   • Existing lookup endpoints, association filtering, payload sanitisation,
//     self-parent protection, update endpoint, refresh and close behaviour are preserved.

import React, { useEffect, useState } from "react";
import ModalWrapper from "./ModalWrapper";
import axiosInstance from "../api/axiosInstance";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";

const toIntOrNull = (v) => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const CATEGORY_OPTIONS = [
  "Limited",
  "Charity",
  "Transport Authority",
  "Partnership",
  "Sole Trader",
  "LLP",
  "Public Sector",
];

const EditCustomerModal = ({ customer, onClose }) => {
  const [form, setForm] = useState(null);
  const { triggerRefresh } = useDashboardRefresh();

  // Lookups for selects
  const [resourceOptions, setResourceOptions] = useState([]); // ⬅️ resources, not users
  const [contactOptions, setContactOptions] = useState([]);
  const [parentCustomerOptions, setParentCustomerOptions] = useState([]);

  // Load lookups (resources, contacts, customers) once the modal opens
  useEffect(() => {
    let cancelled = false;

    const loadLookups = async () => {
      try {
        // Use trailing slash for resources to avoid any 307 redirect issues (mirrors users pattern)
        const [rRes, cRes, custRes] = await Promise.all([
          axiosInstance.get("resources/?limit=1000"),
          axiosInstance.get("contacts?limit=1000"),
          axiosInstance.get("customers?limit=1000"),
        ]);

        if (cancelled) return;

        // Resources: internal staff / contractors
        const resources = (rRes.data || []).map((r) => ({
          id: r.id,
          label:
            [r.first_name, r.last_name].filter(Boolean).join(" ").trim() ||
            r.email ||
            `Resource #${r.id}`,
        }));
        setResourceOptions(resources);

        // Customers (for parent select)
        const customers = (custRes.data || []).map((c) => ({
          id: c.id,
          label: c.name || `Customer #${c.id}`,
        }));
        setParentCustomerOptions(customers);

        // Contacts (filter to this customer if editing)
        const allContacts = (cRes.data || []).map((c) => ({
          id: c.id,
          customer_id: c.customer_id,
          label:
            [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
            c.email ||
            `Contact #${c.id}`,
        }));

        const mine = customer?.id
          ? allContacts.filter((c) => Number(c.customer_id) === Number(customer.id))
          : allContacts;

        setContactOptions(mine);
      } catch (e) {
        if (!cancelled) {
          console.warn(
            "Lookup load failed (resources/contacts/customers):",
            e?.response?.data || e?.message || e
          );
          setResourceOptions([]);
          setContactOptions([]);
          setParentCustomerOptions([]);
        }
      }
    };

    loadLookups();
    return () => {
      cancelled = true;
    };
  }, [customer?.id]);

  useEffect(() => {
    if (customer && typeof customer === "object") {
      setForm({
        // Align with CustomerFormWidget fields and defaults
        name: "",
        nickname: "",
        website: "",
        email: "",
        phone_number: "",
        status: "Active",
        is_vendor: false,
        notes: "",

        // Trading address
        address_line_1: "",
        address_line_2: "",
        address_line_3: "",
        city: "",
        county: "",
        postcode: "",
        country: "United Kingdom",

        // Registered office
        reg_address_line_1: "",
        reg_address_line_2: "",
        reg_address_line_3: "",
        reg_city: "",
        reg_county: "",
        reg_postcode: "",

        // Company info
        company_number: "",
        sic_code: "",
        nature_of_business: "",
        formerly_known_as: "",
        customer_since: "", // yyyy-mm-dd
        category: "",

        // Associations (IDs -> resources/contacts/customers)
        account_manager_id: null,
        closest_resource_id: null,
        parent_customer_id: null,
        primary_contact_id: null,
        billing_contact_id: null,

        // Merge server values last
        ...customer,
        customer_since: customer?.customer_since
          ? String(customer.customer_since).slice(0, 10)
          : "",
        status: customer?.status || "Active",
      });
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [customer, onClose]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const sanitizePayload = (raw) => {
    // Convert blank strings to null where sensible, keep booleans/nums intact
    const payload = { ...raw };

    // Numeric FKs
    payload.account_manager_id = toIntOrNull(payload.account_manager_id);
    payload.closest_resource_id = toIntOrNull(payload.closest_resource_id);
    payload.parent_customer_id = toIntOrNull(payload.parent_customer_id);
    payload.primary_contact_id = toIntOrNull(payload.primary_contact_id);
    payload.billing_contact_id = toIntOrNull(payload.billing_contact_id);

    // Avoid self-reference as parent
    if (payload?.id && payload.parent_customer_id === payload.id) {
      payload.parent_customer_id = null;
    }

    // Normalize blanks to null for optional text fields
    const nullableKeys = [
      "nickname",
      "website",
      "email",
      "phone_number",
      "address_line_1",
      "address_line_2",
      "address_line_3",
      "city",
      "county",
      "postcode",
      "country",
      "reg_address_line_1",
      "reg_address_line_2",
      "reg_address_line_3",
      "reg_city",
      "reg_county",
      "reg_postcode",
      "company_number",
      "sic_code",
      "nature_of_business",
      "formerly_known_as",
      "category",
      "notes",
    ];
    nullableKeys.forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });

    // Customer since -> null if blank
    if (!payload.customer_since) payload.customer_since = null;

    return payload;
  };

  const handleUpdate = async () => {
    try {
      if (!form?.id) throw new Error("Missing customer ID");

      const payload = sanitizePayload({
        id: form.id, // for self-parent check
        // Core identity
        name: form.name || "",
        nickname: form.nickname ?? "",
        website: form.website ?? "",
        email: form.email ?? "",
        phone_number: form.phone_number ?? "",

        // Trading address
        address_line_1: form.address_line_1 ?? "",
        address_line_2: form.address_line_2 ?? "",
        address_line_3: form.address_line_3 ?? "",
        city: form.city ?? "",
        county: form.county ?? "",
        postcode: form.postcode ?? "",
        country: form.country ?? "United Kingdom",

        // Registered office
        reg_address_line_1: form.reg_address_line_1 ?? "",
        reg_address_line_2: form.reg_address_line_2 ?? "",
        reg_address_line_3: form.reg_address_line_3 ?? "",
        reg_city: form.reg_city ?? "",
        reg_county: form.reg_county ?? "",
        reg_postcode: form.reg_postcode ?? "",

        // Company info
        company_number: form.company_number ?? "",
        sic_code: form.sic_code ?? "",
        nature_of_business: form.nature_of_business ?? "",
        formerly_known_as: form.formerly_known_as ?? "",
        customer_since: form.customer_since || "", // YYYY-MM-DD or ""
        category: form.category ?? "",
        is_vendor: form.is_vendor === true,

        // Associations (IDs) — these are RESOURCE IDs now
        account_manager_id: form.account_manager_id,
        closest_resource_id: form.closest_resource_id,
        parent_customer_id: form.parent_customer_id,
        primary_contact_id: form.primary_contact_id,
        billing_contact_id: form.billing_contact_id,

        // Admin
        status: form.status || "Active",
        notes: form.notes ?? "",
      });

      await axiosInstance.put(`customers/${form.id}`, payload);

      alert("Customer updated successfully");
      triggerRefresh();
      onClose?.();
    } catch (err) {
      console.error(
        "Customer update failed:",
        err?.response?.data || err?.message || err
      );
      alert("Failed to update customer.");
    }
  };

  if (!form) return null;

  return (
    <ModalWrapper onClose={onClose}>
      <h3 className="mb-4 break-words text-xl font-semibold">
        Edit Customer: {form.name || "Unknown"}
      </h3>

        {/* Vendor eligibility is independent of CRM lifecycle. */}
        <label className="mb-4 flex min-h-[44px] items-center gap-3 rounded border p-3">
          <input
            type="checkbox"
            checked={form.is_vendor === true}
            onChange={(event) => handleChange("is_vendor", event.target.checked)}
            className="h-5 w-5"
          />
          <span>
            <strong>Is Vendor</strong>
            <span className="block text-sm opacity-75">
              Eligible for vendor price books and supplier selection. Customer lifecycle is unchanged.
            </span>
          </span>
        </label>

        {/* Company */}
      <div className="mb-4 flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,0.4fr)]">
          <input
            type="text"
            value={form.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Company Name"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
            required
          />
          <input
            type="text"
            value={form.nickname || ""}
            onChange={(e) => handleChange("nickname", e.target.value)}
            placeholder="Nickname"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            type="url"
            value={form.website || ""}
            onChange={(e) => handleChange("website", e.target.value)}
            placeholder="Website (https://...)"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="email"
            value={form.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="General Email"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="tel"
            value={form.phone_number || ""}
            onChange={(e) => handleChange("phone_number", e.target.value)}
            placeholder="Telephone Number"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            type="text"
            value={form.company_number || ""}
            onChange={(e) => handleChange("company_number", e.target.value)}
            placeholder="Company Number"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.sic_code || ""}
            onChange={(e) => handleChange("sic_code", e.target.value)}
            placeholder="SIC Code"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <select
            value={form.category || ""}
            onChange={(e) => handleChange("category", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Category…</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <input
            type="date"
            value={form.customer_since || ""}
            onChange={(e) => handleChange("customer_since", e.target.value)}
            placeholder="Customer Since"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.formerly_known_as || ""}
            onChange={(e) => handleChange("formerly_known_as", e.target.value)}
            placeholder="Formerly Known As"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>

        <textarea
          value={form.nature_of_business || ""}
          onChange={(e) => handleChange("nature_of_business", e.target.value)}
          placeholder="Nature of Business"
          rows={2}
          className="min-h-[88px] w-full resize-y rounded border p-2"
        />
      </div>

      {/* Trading Address */}
      <div className="mb-4 flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            type="text"
            value={form.address_line_1 || ""}
            onChange={(e) => handleChange("address_line_1", e.target.value)}
            placeholder="Address Line 1"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.address_line_2 || ""}
            onChange={(e) => handleChange("address_line_2", e.target.value)}
            placeholder="Address Line 2"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.address_line_3 || ""}
            onChange={(e) => handleChange("address_line_3", e.target.value)}
            placeholder="Address Line 3"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <input
            type="text"
            value={form.city || ""}
            onChange={(e) => handleChange("city", e.target.value)}
            placeholder="City"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.county || ""}
            onChange={(e) => handleChange("county", e.target.value)}
            placeholder="County / Province"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.postcode || ""}
            onChange={(e) => handleChange("postcode", e.target.value)}
            placeholder="Postcode / Zip"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.country || "United Kingdom"}
            onChange={(e) => handleChange("country", e.target.value)}
            placeholder="Country"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>
      </div>

      {/* Registered Office */}
      <div className="mb-4 flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            type="text"
            value={form.reg_address_line_1 || ""}
            onChange={(e) => handleChange("reg_address_line_1", e.target.value)}
            placeholder="Registered Address Line 1"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.reg_address_line_2 || ""}
            onChange={(e) => handleChange("reg_address_line_2", e.target.value)}
            placeholder="Registered Address Line 2"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.reg_address_line_3 || ""}
            onChange={(e) => handleChange("reg_address_line_3", e.target.value)}
            placeholder="Registered Address Line 3"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            type="text"
            value={form.reg_city || ""}
            onChange={(e) => handleChange("reg_city", e.target.value)}
            placeholder="Registered City"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.reg_county || ""}
            onChange={(e) => handleChange("reg_county", e.target.value)}
            placeholder="Registered County"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
          <input
            type="text"
            value={form.reg_postcode || ""}
            onChange={(e) => handleChange("reg_postcode", e.target.value)}
            placeholder="Registered Postcode"
            className="min-h-[44px] w-full min-w-0 rounded border p-2"
          />
        </div>
      </div>

      {/* Links & Admin */}
      <div className="flex flex-col gap-3">
        {/* Resources & Parent customer */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <select
            value={form.account_manager_id ?? ""}
            onChange={(e) => handleChange("account_manager_id", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Account Manager…</option>
            {resourceOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>

          <select
            value={form.closest_resource_id ?? ""}
            onChange={(e) => handleChange("closest_resource_id", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Closest Resource…</option>
            {resourceOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>

          <select
            value={form.parent_customer_id ?? ""}
            onChange={(e) => handleChange("parent_customer_id", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Parent Company…</option>
            {parentCustomerOptions
              .filter((c) => !form?.id || c.id !== form.id)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
          </select>
        </div>

        {/* Contacts for this customer */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <select
            value={form.primary_contact_id ?? ""}
            onChange={(e) => handleChange("primary_contact_id", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Primary Contact…</option>
            {contactOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>

          <select
            value={form.billing_contact_id ?? ""}
            onChange={(e) => handleChange("billing_contact_id", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="">Billing Contact…</option>
            {contactOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>

          <select
            value={form.status || "Active"}
            onChange={(e) => handleChange("status", e.target.value)}
            className="min-h-[44px] w-full min-w-0 rounded border bg-white p-2"
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <textarea
          value={form.notes || ""}
          onChange={(e) => handleChange("notes", e.target.value)}
          placeholder="Notes"
          rows={3}
          className="min-h-[96px] w-full resize-y rounded border p-2"
        />
      </div>

      <div className="mt-5 grid grid-cols-1 sm:flex sm:justify-end">
        <button
          onClick={handleUpdate}
          className="min-h-[44px] w-full touch-manipulation rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow transition-colors duration-200 hover:bg-btnActionHover active:bg-btnActionClick sm:w-auto"
        >
          Update Customer
        </button>
      </div>
    </ModalWrapper>
  );
};

export default EditCustomerModal;
